'use client';

import type { ReactNode } from 'react';
import { ResultsInteractionProvider, type NotificationFunction } from '../../../lotteries/ResultsSection/context/ResultsInteractionContext';
import { ParticipationInfo } from '../../ParticipationInfo/ParticipationInfo';
import { shouldShowWinnings } from '../../utils/hasWinnings/hasWinnings';
import { usePurchaseDetails } from '../context/PurchaseDetailsContext';

/**
 * Renders participation results for the selected draw, followed by `children` (the played numbers block).
 * Reads its data from `PurchaseDetailsProvider`, so it must be rendered inside it.
 */
type PurchaseDetailsResultsProps = {
  children?: ReactNode;
};

function PurchaseDetailsResults({ children }: PurchaseDetailsResultsProps) {
  const { ticket, lotteryName, selectedDate, activeParticipation, dyjPlayed, handleDateSelect, showOlderParticipationsInfo, setShowOlderParticipationsInfo } = usePurchaseDetails();
  const { participationInfo, currency, options, hasMoreHistoricalParticipations } = ticket;

  const shouldShowNotification: NotificationFunction = gameName => {
    const { participationState, realWinning, lineWinningInfo } = activeParticipation;
    const gameWinnings = lineWinningInfo[gameName as keyof typeof lineWinningInfo];

    return shouldShowWinnings(participationState, gameWinnings, realWinning);
  };

  return (
    <div className="contents">
      <ResultsInteractionProvider shouldShowNotification={shouldShowNotification}>
        <ParticipationInfo
          lotteryName={lotteryName}
          participations={participationInfo}
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          dyjPlayed={dyjPlayed}
          currency={currency}
          options={options}
          hasMoreHistoricalParticipations={hasMoreHistoricalParticipations}
          showOlderParticipationsInfo={showOlderParticipationsInfo}
          setShowOlderParticipationsInfo={setShowOlderParticipationsInfo}
          className="ll-container-full"
        />
        {!showOlderParticipationsInfo && (
          <>
            <hr className="ll-container-inner h-px border-t-border" />
            {children}
          </>
        )}
      </ResultsInteractionProvider>
    </div>
  );
}

export { PurchaseDetailsResults };
